/**
 * ProductOverviewTab V2 — Redesigned
 *
 * Two-column layout:
 * Left (2/3): Content Status + Channels overview
 * Right (1/3): Readiness ring + Quick Info + Sales
 */

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { Product } from '@/types/products';
import { useProductContentBlocks } from '@/hooks/useContentBlock';
import { useProductChannelPackages } from '@/hooks/useChannelPackage';
import { ScheduleBanner } from '@/components/publishing';
import { useActiveSchedule } from '@/hooks/useActiveSchedule';
import {
  ContentStatusCard,
  ReadinessRingCard,
  ProductQuickInfoCard,
  ProductSalesCard,
  ChannelsOverviewCard,
} from './overview';

interface ProductOverviewTabProps {
  product: Product;
}

export function ProductOverviewTab({ product }: ProductOverviewTabProps) {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  const contentBlocksQuery = useProductContentBlocks(product.id);
  const channelPackagesQuery = useProductChannelPackages(product.id);
  const contentBlocks = contentBlocksQuery.data?.content_blocks ?? [];

  const firstBlockId = contentBlocks[0]?.id;
  const activeScheduleQuery = useActiveSchedule({
    entityType: 'content_block',
    entityId: firstBlockId || '',
    enabled: !!firstBlockId,
  });

  // Readiness steps
  const hasContent = contentBlocks.some((b) => b.current_version?.content);
  const hasImages = !!product.first_image_id;
  const hasFamily = !!product.family_id;
  const channelPackages = channelPackagesQuery.data?.channel_packages ?? [];
  const hasChannels = channelPackages.length > 0;

  const readinessSteps = [
    { label: t('products.readiness.productSynced'), done: true },
    { label: t('products.readiness.uploadImages'), done: hasImages },
    { label: t('products.readiness.createContent'), done: hasContent },
    { label: t('products.readiness.assignFamily'), done: hasFamily },
    { label: t('products.readiness.configureChannel'), done: hasChannels },
    { label: t('products.readiness.passQuality'), done: false },
  ];
  const doneCount = readinessSteps.filter((s) => s.done).length;
  const readinessPercent = Math.round((doneCount / readinessSteps.length) * 100);

  const navigateTab = (tab: string) => setSearchParams({ tab });

  return (
    <section className="product-overview-tab space-y-4" aria-label={t('common.overview')}>
      {activeScheduleQuery.data?.schedule && (
        <ScheduleBanner schedule={activeScheduleQuery.data.schedule} />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          <ContentStatusCard contentBlocks={contentBlocks} onEditContent={() => navigateTab('content')} />
          <ChannelsOverviewCard
            channelCount={channelPackages.length}
            onViewChannels={() => navigateTab('channels')}
            onAddChannel={() => navigateTab('channels')}
          />
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <ReadinessRingCard progressPercent={readinessPercent} steps={readinessSteps} />
          <ProductQuickInfoCard product={product} />
          <ProductSalesCard productId={product.id} />
        </div>
      </div>
    </section>
  );
}
